// src/components/LinksManagerSortableLink.js
import React, { useState } from "react";
import { useSortable } from "@dnd-kit/sortable";
import {
  Pencil,
  X,
  Eye,
  EyeOff,
  Ghost,
  Pin,
  Image as ImageIcon,
  Copy,
} from "lucide-react";
import styles from "./LinksManager.module.css";
import LinksManagerDragHandle from "./LinksManagerDragHandle";

// SortableLink Component
function LinksManagerSortableLink({
  link,
  isProUser,
  onUpgradeClick,
  onToggleVisibility,
  onToggleGhost,
  onTogglePin,
  onStartEditing,
  onRemove,
  onImageUpload,
  onImageRemove,
  onCopyPassword,
  handleFreeFeatureAttempt,
  isUploading,
  statusLabel,
  animatingLinkId,
}) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: link.id });

  // Add state for image hover
  const [isImageHovered, setIsImageHovered] = useState(false);

  const style = {
    transform: transform
      ? `translate3d(${transform.x}px, ${transform.y}px, 0)`
      : undefined,
    transition: isDragging ? "none" : transition,
    width: "100%",
  };

  return (
    <div
      ref={setNodeRef}
      style={style}
      {...attributes}
      className={`${styles.draggableContainer} ${
        isDragging ? styles.dragging : ""
      } 
                 ${link.hidden ? styles.hiddenLink : ""} 
                 ${link.ghostProtected ? styles.ghostProtected : ""}
                 ${link.pinned ? styles.pinnedLink : ""}
                 ${animatingLinkId === link.id ? styles.animatingLink : ""}`}
    >
      {/* Status indicator */}
      <div
        className={`${styles.statusIndicator} ${
          statusLabel && statusLabel.linkId === link.id
            ? styles.statusVisible
            : ""
        }`}
      >
        {statusLabel && statusLabel.linkId === link.id ? statusLabel.text : ""}
      </div>

      <div {...listeners}>
        <LinksManagerDragHandle
          isProUser={isProUser}
          onUpgradeClick={() => handleFreeFeatureAttempt(link.id)}
          dragHandleProps={listeners}
          isDragging={isDragging}
        />
      </div>

      <div className={styles.linkInfo}>
        <div className={styles.linkTitle}>
          {link.imageUrl && (
            <div
              className={styles.linkImagePreview}
              onMouseEnter={() => setIsImageHovered(true)}
              onMouseLeave={() => setIsImageHovered(false)}
            >
              <img src={link.imageUrl} alt="" />
              {isImageHovered && isProUser && (
                <button
                  className={styles.removeImageButton}
                  onClick={(e) => {
                    e.stopPropagation();
                    onImageRemove(link.id);
                  }}
                  title="Remove image"
                >
                  <X size={14} />
                </button>
              )}
            </div>
          )}
          <span>{link.title}</span>
        </div>

        {/* Ghost Password Display - Always show password if ghost protected */}
        {link.ghostProtected && link.ghostPassword && (
          <div className={styles.ghostPasswordContainer}>
            <div className={styles.ghostPasswordContent}>
              <span>Password:</span>
              <code className={styles.ghostPasswordCode}>
                {link.ghostPassword}
              </code>
            </div>
            <button
              className={styles.copyButton}
              onClick={() => onCopyPassword(link.ghostPassword)}
              title="Copy password"
            >
              <Copy size={14} />
            </button>
          </div>
        )}
      </div>

      {/* Link actions - in TWO ROWS of three icons each */}
      <div className={styles.linkActionsContainer}>
        {/* First row of action buttons */}
        <div className={styles.linkActions}>
          {/* Pin button */}
          <button
            onClick={() => {
              if (!isProUser) {
                handleFreeFeatureAttempt(link.id);
              } else {
                onTogglePin(link.id);
              }
            }}
            className={`${styles.actionButton} ${styles.pinButton} ${
              link.pinned ? styles.active : ""
            } ${!isProUser ? styles.disabled : ""}`}
            title={
              isProUser ? (link.pinned ? "Unpin" : "Pin to top") : "Pro feature"
            }
          >
            <Pin size={14} />
          </button>

          {/* Ghost button */}
          <button
            onClick={() => {
              if (!isProUser) {
                handleFreeFeatureAttempt(link.id);
              } else if (!link.hidden) {
                onToggleGhost(link.id);
              }
            }}
            className={`${styles.actionButton} ${styles.ghostButton} ${
              link.ghostProtected ? styles.active : ""
            } ${!isProUser ? styles.disabled : ""}`}
            disabled={link.hidden}
            title={
              link.hidden ? "Cannot ghost protect hidden links" : "Pro feature"
            }
          >
            <Ghost size={14} />
          </button>

          {/* Visibility button */}
          <button
            onClick={() => {
              if (!isProUser) {
                handleFreeFeatureAttempt(link.id);
              } else {
                onToggleVisibility(link.id);
              }
            }}
            className={`${styles.actionButton} ${styles.visibilityButton} ${
              link.hidden ? styles.active : ""
            } ${!isProUser ? styles.disabled : ""}`}
            title={
              isProUser
                ? link.hidden
                  ? "Show Link"
                  : "Hide Link"
                : "Pro feature"
            }
          >
            {link.hidden ? <EyeOff size={14} /> : <Eye size={14} />}
          </button>
        </div>

        {/* Second row of action buttons */}
        <div className={styles.linkActions}>
          {/* Edit button */}
          <button
            onClick={() => onStartEditing(link)}
            className={`${styles.actionButton} ${styles.editButton}`}
          >
            <Pencil size={14} />
          </button>

          {/* Delete button */}
          <button
            onClick={() => onRemove(link.id)}
            className={`${styles.actionButton} ${styles.removeButton}`}
          >
            <X size={14} />
          </button>

          {/* Image button */}
          <button
            onClick={() => {
              if (!isProUser) {
                handleFreeFeatureAttempt(link.id);
              } else {
                // Always open file browser directly
                onImageUpload(link.id);
              }
            }}
            className={`${styles.actionButton} ${styles.imageButton} ${
              link.imageUrl ? styles.active : ""
            } ${!isProUser ? styles.disabled : ""}`}
            title={
              isProUser
                ? link.imageUrl
                  ? "Change Image (max 800×800px)"
                  : "Add Image (max 800×800px)"
                : "Pro feature"
            }
            disabled={isUploading === link.id}
          >
            <ImageIcon size={14} />
            {isUploading === link.id && (
              <span className={styles.spinnerSmall}></span>
            )}
          </button>
        </div>
      </div>
    </div>
  );
}

export default LinksManagerSortableLink;